/**
 * Hook for managing file attachments in the chatbot
 * Handles file selection, upload to S3, and state management
 */

import { useState, useCallback, useRef } from 'react';
import * as api from '../../services/apiClient';
import { logger } from '../../utils/logger';

// Allowed MIME types for chat attachments (PDF and DOCX only)
const ALLOWED_MIME_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
];

// File extension to MIME type mapping for validation
const EXTENSION_TO_MIME: Record<string, string> = {
  '.pdf': 'application/pdf',
  '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
};

// Maximum file size: 10MB
const MAX_FILE_SIZE = 10 * 1024 * 1024;

export interface AttachedFile {
  id: string;
  file: File;
  name: string;
  status: 'pending' | 'uploading' | 'uploaded' | 'error';
  progress: number;
  fileKey?: string;
  error?: string;
}

export interface UseFileAttachmentsReturn {
  attachedFiles: AttachedFile[];
  addFiles: (files: FileList | File[]) => void;
  removeFile: (id: string) => void;
  uploadFiles: () => Promise<string[]>;
  clearFiles: () => void;
  isUploading: boolean;
  hasFiles: boolean;
}

/**
 * Validate file type against whitelist
 */
function isValidFileType(file: File): boolean {
  // Check MIME type first
  if (ALLOWED_MIME_TYPES.includes(file.type)) {
    return true;
  }

  // Fallback to extension check for edge cases
  const extension = '.' + file.name.split('.').pop()?.toLowerCase();
  return extension in EXTENSION_TO_MIME;
}

/**
 * Get MIME type for a file (fallback to extension if needed)
 */
function getContentType(file: File): string {
  if (file.type && ALLOWED_MIME_TYPES.includes(file.type)) {
    return file.type;
  }

  const extension = '.' + file.name.split('.').pop()?.toLowerCase();
  return EXTENSION_TO_MIME[extension] || 'application/octet-stream';
}

/**
 * Hook to manage file attachments for chat messages
 */
export function useFileAttachments(): UseFileAttachmentsReturn {
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const isUploadingRef = useRef(false);
  const uploadAbortControllerRef = useRef<AbortController | null>(null);

  /**
   * Add files to the attachment list
   */
  const addFiles = useCallback((files: FileList | File[]) => {
    const fileArray = Array.from(files);

    const newAttachments: AttachedFile[] = [];
    const errors: string[] = [];

    fileArray.forEach((file) => {
      if (!isValidFileType(file)) {
        errors.push(`${file.name}: type de fichier non autorise (PDF ou DOCX uniquement)`);
        return;
      }

      // Check file size (max 10MB)
      if (file.size > MAX_FILE_SIZE) {
        const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
        errors.push(`${file.name}: fichier trop volumineux (${sizeMB} Mo, max 10 Mo)`);
        return;
      }

      // Check for duplicates
      const isDuplicate = attachedFiles.some(
        (existing) => existing.name === file.name && existing.file.size === file.size
      );

      if (isDuplicate) {
        errors.push(`${file.name}: fichier deja ajoute`);
        return;
      }

      newAttachments.push({
        id: `file-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        file,
        name: file.name,
        status: 'pending',
        progress: 0
      });
    });

    if (errors.length > 0) {
      logger.warn('Some files were rejected:', errors);
    }

    if (newAttachments.length > 0) {
      setAttachedFiles((prev) => [...prev, ...newAttachments]);
    }
  }, [attachedFiles]);

  /**
   * Remove a file from the attachment list
   */
  const removeFile = useCallback((id: string) => {
    setAttachedFiles((prev) => prev.filter((file) => file.id !== id));
  }, []);

  /**
   * Upload all pending files to S3
   * Returns array of file_keys for successfully uploaded files
   */
  const uploadFiles = useCallback(async (): Promise<string[]> => {
    // Re-entry guard: if already uploading, return existing uploaded keys
    if (isUploadingRef.current) {
      logger.warn('[useFileAttachments] uploadFiles re-entered while already uploading');
      return attachedFiles
        .filter((f) => f.status === 'uploaded' && f.fileKey)
        .map((f) => f.fileKey!);
    }

    const pendingFiles = attachedFiles.filter((f) => f.status === 'pending');
    if (pendingFiles.length === 0) {
      return attachedFiles
        .filter((f) => f.status === 'uploaded' && f.fileKey)
        .map((f) => f.fileKey!);
    }

    isUploadingRef.current = true;
    setIsUploading(true);
    uploadAbortControllerRef.current = new AbortController();

    const uploadedKeys: string[] = [];

    try {
      for (const attachment of pendingFiles) {
        try {
          // Update status to uploading
          setAttachedFiles((prev) =>
            prev.map((f) =>
              f.id === attachment.id ? { ...f, status: 'uploading' as const, progress: 0 } : f
            )
          );

          const contentType = getContentType(attachment.file);

          // Get presigned URL with chat-attachments prefix
          const urlResponse = await api.generateUploadUrl({
            filename: attachment.name,
            content_type: contentType,
            prefix: 'chat-attachments'
          });

          if (!urlResponse.success || !urlResponse.data) {
            throw new Error(urlResponse.error || 'Failed to generate upload URL');
          }

          const { presigned_url, file_key } = urlResponse.data;

          // Upload to S3 with progress tracking
          const uploadResult = await api.uploadToS3(
            presigned_url,
            attachment.file,
            (progress) => {
              setAttachedFiles((prev) =>
                prev.map((f) =>
                  f.id === attachment.id ? { ...f, progress } : f
                )
              );
            },
            uploadAbortControllerRef.current?.signal,
            contentType
          );

          if (!uploadResult.success) {
            throw new Error(uploadResult.error || 'Upload failed');
          }

          // Update status to uploaded
          setAttachedFiles((prev) =>
            prev.map((f) =>
              f.id === attachment.id
                ? { ...f, status: 'uploaded' as const, progress: 100, fileKey: file_key }
                : f
            )
          );

          uploadedKeys.push(file_key);
          logger.log(`File uploaded successfully: ${attachment.name} -> ${file_key}`);
        } catch (error: any) {
          logger.error(`Failed to upload ${attachment.name}:`, error);

          setAttachedFiles((prev) =>
            prev.map((f) =>
              f.id === attachment.id
                ? { ...f, status: 'error' as const, error: error.message || 'Upload failed' }
                : f
            )
          );
        }
      }

      // Include any previously uploaded files
      const previouslyUploaded = attachedFiles
        .filter((f) => f.status === 'uploaded' && f.fileKey && !pendingFiles.includes(f))
        .map((f) => f.fileKey!);

      return [...previouslyUploaded, ...uploadedKeys];
    } finally {
      isUploadingRef.current = false;
      setIsUploading(false);
      uploadAbortControllerRef.current = null;
    }
  }, [attachedFiles]);

  /**
   * Clear all attachments
   */
  const clearFiles = useCallback(() => {
    // Abort any ongoing uploads
    if (uploadAbortControllerRef.current) {
      uploadAbortControllerRef.current.abort();
      uploadAbortControllerRef.current = null;
    }

    setAttachedFiles([]);
    setIsUploading(false);
  }, []);

  return {
    attachedFiles,
    addFiles,
    removeFile,
    uploadFiles,
    clearFiles,
    isUploading,
    hasFiles: attachedFiles.length > 0
  };
}
